import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mlm/ui';
import { getProductRecipe, listHouseVariants, listProducts, setProductRecipe } from '../../lib/api';
import { ProductPicker } from '../product-picker';

/**
 * One row of the recipe editor. Named apart from the API's `RecipeRow` on
 * purpose (same reason as `ComponentDraft`): it carries DISPLAY-ONLY fields the
 * PUT body has no place for, and they are stripped on save.
 */
type RecipeDraft = {
  materialProductId: string;
  /** One variation of THIS product; null = every size without rows of its own. */
  variantId: string | null;
  qtyPerUnit: number;
  sku: string;
  name: string;
  active: boolean;
  unitLabel: string | null;
  /** A saved row's variation name, kept so an archived variation still labels itself. */
  variantName: string | null;
};

const ALL_SIZES = '__all__';

/**
 * Typed editor for a product's RECIPE: the materials one sold unit uses up (a
 * milkshake takes a cup and a lid). Self-contained: it loads and saves its own
 * rows through `/products/:id/materials`.
 *
 * The one rule worth showing the operator is the variation scope. A size with
 * rows of its own uses ONLY those rows; it does not also inherit the "All sizes"
 * rows. That is what lets a Large take a different cup from the default.
 */
export function RecipeSection({ productId }: { productId: string }) {
  const qc = useQueryClient();
  const recipeQuery = useQuery({
    queryKey: ['product-recipe', productId],
    queryFn: () => getProductRecipe(productId),
  });
  const variantsQuery = useQuery({
    queryKey: ['house-variants', productId],
    queryFn: () => listHouseVariants(productId),
  });
  // Unit labels for a material that was just picked (a saved row carries its
  // own). One page is enough to label a typical tenant's materials; past it a
  // new row simply shows no unit until the recipe is saved and re-read.
  const materialsQuery = useQuery({
    queryKey: ['admin-products', 'recipe-materials'],
    queryFn: () => listProducts({ pageSize: 200, material: 'only' }),
    staleTime: 60_000,
  });
  const unitById = new Map((materialsQuery.data ?? []).map((m) => [m.id, m.unitLabel]));

  const [rows, setRows] = useState<RecipeDraft[] | null>(null);
  const current: RecipeDraft[] =
    rows ??
    (recipeQuery.data ?? []).map((r) => ({
      materialProductId: r.materialProductId,
      variantId: r.variantId,
      qtyPerUnit: r.qtyPerUnit,
      sku: r.sku,
      name: r.name,
      active: r.active,
      unitLabel: r.unitLabel,
      variantName: r.variantName,
    }));

  const variants = (variantsQuery.data ?? []).filter((v) => v.active);
  const hasVariants = variants.length > 0;

  // The server rejects the same material twice within one scope
  // (`duplicate_material`); blocking Save here means the two cannot disagree.
  const seen = new Set<string>();
  let duplicate = false;
  for (const r of current) {
    if (!r.materialProductId) continue;
    const key = `${r.variantId ?? ''}:${r.materialProductId}`;
    if (seen.has(key)) duplicate = true;
    seen.add(key);
  }

  const save = useMutation({
    mutationFn: () =>
      setProductRecipe(
        productId,
        // Project explicitly: the PUT's body schema is non-strict, so stray
        // display fields would be silently dropped rather than erroring.
        current
          .filter((r) => r.materialProductId)
          .map((r) => ({
            materialProductId: r.materialProductId,
            variantId: r.variantId,
            qtyPerUnit: r.qtyPerUnit,
          })),
      ),
    meta: { successMessage: 'Recipe saved.' },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['product-recipe', productId] });
      // Drop the draft so the saved rows are read back with their joined labels.
      setRows(null);
    },
  });

  const patch = (i: number, next: Partial<RecipeDraft>) =>
    setRows(current.map((x, j) => (j === i ? { ...x, ...next } : x)));

  const noMaterialsYet = materialsQuery.isSuccess && (materialsQuery.data?.length ?? 0) === 0;

  return (
    <div className="space-y-3">
      <div className="space-y-1 text-xs text-muted-foreground">
        <p>
          The materials one sold unit uses up. Selling this product takes them off the branch’s
          shelf, at the till and for online orders alike. Running out of a material never blocks a
          sale: the count stops at zero and the gap shows in the stock log.
        </p>
        {hasVariants && (
          <p>
            A size with rows of its own uses only those rows. A size with none uses the “All sizes”
            rows.
          </p>
        )}
      </div>

      {noMaterialsYet && (
        <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
          There are no materials yet. Add them on the{' '}
          <Link to="/materials" className="underline">
            Materials page
          </Link>{' '}
          first, then come back to build the recipe.
        </p>
      )}

      {recipeQuery.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="border-b text-left text-xs text-muted-foreground">
            <tr>
              {hasVariants && <th className="w-48 py-1">Applies to</th>}
              <th className="py-1">Material</th>
              <th className="w-40 py-1">Qty per unit sold</th>
              <th className="w-16 py-1" />
            </tr>
          </thead>
          <tbody>
            {current.length === 0 && (
              <tr>
                <td
                  colSpan={hasVariants ? 4 : 3}
                  className="py-3 text-center text-xs text-muted-foreground"
                >
                  No materials. This product uses nothing up when it sells.
                </td>
              </tr>
            )}
            {current.map((r, i) => {
              const unit = r.unitLabel ?? unitById.get(r.materialProductId) ?? null;
              // A saved row may name a variation that has since been hidden;
              // keep it selectable so the row still reads as what was saved.
              const orphanVariant =
                r.variantId && !variants.some((v) => v.id === r.variantId) ? r.variantId : null;
              return (
                <tr key={i} className="border-b align-top last:border-0">
                  {hasVariants && (
                    <td className="py-1 pr-2">
                      <Select
                        value={r.variantId ?? ALL_SIZES}
                        onValueChange={(v) =>
                          patch(i, { variantId: v === ALL_SIZES ? null : v, variantName: null })
                        }
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={ALL_SIZES}>All sizes</SelectItem>
                          {variants.map((v) => (
                            <SelectItem key={v.id} value={v.id}>
                              {v.name}
                            </SelectItem>
                          ))}
                          {orphanVariant && (
                            <SelectItem value={orphanVariant}>
                              {r.variantName ?? 'Hidden size'} (hidden)
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </td>
                  )}
                  <td className="py-1 pr-2">
                    <ProductPicker
                      selected={
                        r.materialProductId
                          ? { id: r.materialProductId, sku: r.sku, name: r.name, active: r.active }
                          : null
                      }
                      onChange={(p) =>
                        patch(i, {
                          materialProductId: p?.id ?? '',
                          sku: p?.sku ?? '',
                          name: p?.name ?? '',
                          active: p?.active ?? true,
                          unitLabel: p ? (unitById.get(p.id) ?? null) : null,
                        })
                      }
                      // Never the product being edited: a product cannot use
                      // itself up.
                      excludeIds={[productId]}
                      filter={{ activeOnly: true, material: 'only' }}
                      annotate={(p) =>
                        current.some(
                          (x, j) =>
                            j !== i && x.materialProductId === p.id && x.variantId === r.variantId,
                        )
                          ? 'already in this recipe'
                          : null
                      }
                      placeholder="Search material by name or SKU…"
                    />
                  </td>
                  <td className="py-1 pr-2">
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="1"
                        step="1"
                        value={r.qtyPerUnit}
                        onChange={(e) =>
                          patch(i, {
                            qtyPerUnit: Math.max(1, Math.floor(Number(e.target.value)) || 1),
                          })
                        }
                        className="h-9"
                      />
                      <span className="w-10 shrink-0 text-xs text-muted-foreground">
                        {unit ?? 'pcs'}
                      </span>
                    </div>
                  </td>
                  <td className="py-1">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setRows(current.filter((_, j) => j !== i))}
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {duplicate && (
        <p className="text-xs text-destructive">
          The same material is listed twice for the same size. Combine the two rows.
        </p>
      )}

      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() =>
            setRows([
              ...current,
              {
                materialProductId: '',
                variantId: null,
                qtyPerUnit: 1,
                sku: '',
                name: '',
                active: true,
                unitLabel: null,
                variantName: null,
              },
            ])
          }
        >
          + Add material
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={save.isPending || duplicate || rows === null}
          onClick={() => save.mutate()}
        >
          {save.isPending ? 'Saving…' : 'Save recipe'}
        </Button>
      </div>
    </div>
  );
}
